import React, { useState } from 'react';
import Swal from 'sweetalert2';

import { ContractDA } from '../../../services/admin/contract.service';
import { Contract } from '../../../models/employee.model';
import { modalShow, modalHide } from '../../../utils/modal.util';

export const TERMINATE_MODAL_ID = 'modal-terminate-contract';

const END_REASONS = [
  'resignation',
  'dismissal',
  'mutual_agreement',
  'end_of_term',
  'retirement',
  'death',
  'disability',
  'probation',
] as const;

interface TerminateContractModalProps {
  companyId: string;
  employeeId: string;
  contract: Contract;
  onSuccess: () => void;
  onCancel: () => void;
  lang: any;
  autoOpen?: boolean;
}

const TerminateContractModal: React.FC<TerminateContractModalProps> = ({
  companyId, employeeId, contract, onSuccess, onCancel, lang, autoOpen = false,
}) => {
  const [endDate, setEndDate] = useState('');
  const [endReason, setEndReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (autoOpen) {
      modalShow(TERMINATE_MODAL_ID);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reasonLabel = (reason: string) => {
    return lang.labels[`endReason_${reason}`] || reason.replace(/_/g, ' ');
  };

  const closeModalThen = (callback: () => void) => {
    const element = document.getElementById(TERMINATE_MODAL_ID);
    if (element) {
      element.addEventListener('hidden.bs.modal', callback, { once: true });
    }
    modalHide(TERMINATE_MODAL_ID);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!endDate || !endReason) return;

    const confirmed = await Swal.fire({
      title: lang.labels.confirmTermination || 'Confirm termination',
      html: `
        <div class="text-start">
          <p>${lang.labels.terminationConsequences || 'Terminating this contract will have the following effects:'}</p>
          <ul>
            <li>${lang.labels.terminationStatusChange || 'The employee status will change to <strong>terminated</strong>'}</li>
            <li>${lang.labels.terminationFinalSettlement || 'A <strong>final settlement</strong> will be produced on the payroll period containing this date'}</li>
            <li>${lang.labels.terminationExclusion || 'The employee will be <strong>excluded from subsequent payroll runs</strong>, except for retroactive adjustments'}</li>
          </ul>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.confirm || 'Confirm',
      cancelButtonText: lang.labels.cancel || 'Cancel',
      confirmButtonColor: '#d33',
    });

    if (!confirmed.isConfirmed) return;

    setSubmitting(true);

    const response = await ContractDA.terminateContract(
      companyId, employeeId, contract.id,
      { end_date: endDate, end_reason: endReason },
    );

    if (response.status === 200) {
      Swal.fire({
        title: lang.labels.contractTerminated || 'Contract terminated',
        icon: 'success',
        showConfirmButton: false,
        timer: 1500,
      });
      closeModalThen(() => onSuccess());
    } else {
      Swal.fire({
        title: lang.labels.error || 'Error',
        text: response.data?.message || response.message || 'An error occurred',
        icon: 'error',
      });
    }

    setSubmitting(false);
  };

  const handleCancel = () => {
    closeModalThen(() => onCancel());
  };

  return (
    <div id={TERMINATE_MODAL_ID} className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="fw-bolder">{lang.labels.terminateContract || 'Terminate contract'}</h2>
            <div className="btn btn-sm btn-icon btn-active-color-primary" onClick={handleCancel}>
              <span className="svg-icon svg-icon-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black" />
                  <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black" />
                </svg>
              </span>
            </div>
          </div>
          <div className="modal-body">
            <div className="alert alert-danger mb-5">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {lang.labels.terminationWarning || 'Terminating a contract triggers a final settlement on the payroll period containing the end date. This action can be cancelled before the payroll run is validated.'}
            </div>

            <form id="terminate-form" onSubmit={handleSubmit} noValidate>
              <div className="fv-row mb-5">
                <label className="form-label required">{lang.labels.endDate || 'End date'}</label>
                <input
                  className="form-control"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                />
              </div>

              <div className="fv-row mb-5">
                <label className="form-label required">{lang.labels.endReason || 'End reason'}</label>
                <select
                  className="form-select"
                  value={endReason}
                  onChange={(e) => setEndReason(e.target.value)}
                  required
                >
                  <option value="">{lang.labels.select || 'Select...'}</option>
                  {END_REASONS.map((reason) => (
                    <option key={reason} value={reason}>{reasonLabel(reason)}</option>
                  ))}
                </select>
              </div>
            </form>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-light me-3" type="button" onClick={handleCancel}>
              {lang.labels.cancel || 'Cancel'}
            </button>
            <button
              className="btn btn-danger"
              type="submit"
              form="terminate-form"
              disabled={submitting || !endDate || !endReason}
            >
              {submitting && <span className="spinner-border spinner-border-sm me-2"></span>}
              {lang.labels.terminateContract || 'Terminate contract'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TerminateContractModal;
